// Foundations
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Output,
  EventEmitter,
  Input,
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

// rxjs
import { finalize, map } from 'rxjs/operators';

// Services
import { GetNearestFranchisesGQL } from 'src/app/services/graphql.service';
import { CognitoService } from 'src/app/services/cognito.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { RegisterService } from 'src/app/services/register.service';

// Models
import { FranchiseLocation } from '../../../models/franchise.model';

// ENV
import { environment } from '../../../../environments/environment';

const POSTAL_CODE_REGEX =
  /^(\d{5}(\-\d{4})?|[A-Za-z]\d[A-Za-z][ \-]?\d[A-Za-z]\d)$/;

const radius: number = environment.nearestFranchisesSearchRadius;

@Component({
  selector: 'app-camp-location-search-select',
  templateUrl: './camp-location-search-select.component.html',
  styleUrls: ['./camp-location-search-select.component.scss'],
})
export class CampLocationSearchSelectComponent
  implements OnInit, AfterViewInit
{
  @Input() isReg: boolean; // reg flow flag

  @Output() selectedCampId = new EventEmitter<string>();

  @ViewChild('postalCode', { static: false }) postalCode: ElementRef;

  form: FormGroup;

  showResults = false;
  displayInput = '';
  currentSelection: FranchiseLocation;

  radioOptions: Partial<FranchiseLocation>[];

  franchises$: any;

  constructor(
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private getNearestFranchisesQuery: GetNearestFranchisesGQL,
    private registerService: RegisterService,
    private cognitoService: CognitoService,
    private loadingIndicatorService: LoadingIndicatorService
  ) {
    this.form = this.fb.group(
      {
        postalCode: [''],
      },
      { validator: this.checkPostalCodePattern }
    );
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.postalCode.nativeElement.focus();
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f(): any {
    return this.form.controls;
  }

  changeCheckedRadio(franchise: FranchiseLocation): void {
    franchise.selected = !franchise.selected;

    if (!franchise.selected) {
      this.clearCurrentSelection();
      return;
    }

    this.currentSelection = franchise;
    this.selectedCampId.emit(this.currentSelection.id);

    // uncheck all other options
    this.radioOptions.forEach((option) => {
      if (option.id !== franchise.id) {
        option.selected = false;
      }
    });
  }

  onSearch(): void {
    this.postalCode.nativeElement.blur();
    this.clearCurrentSelection();

    if (!this.form.hasError('postalCode')) {
      this.showResults = true;
      this.displayInput = this.form.controls.postalCode.value;

      this.loadingIndicatorService.show();

      if (this.isReg) {
        // in reg flow, sign user in to obtain user session for GraphQL
        // after data is successfully fetched, sign user out
        const { email, password } = this.registerService.readRegisterFormData();
        this.cognitoService
          .signIn({ username: email, password })
          .subscribe((status) => {
            if (status.result === 'SUCCESS') {
              this.fetchData();
            } else {
              this.loadingIndicatorService.hide();
              this.snackBar.open(status.message, '', { duration: 2000 });
            }
          });
      } else {
        this.fetchData();
      }
    } else {
      this.showResults = false;
    }
  }

  clearCurrentSelection(): void {
    this.currentSelection = null;
    this.selectedCampId.emit(null);
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private fetchData() {
    this.getNearestFranchisesQuery
      .fetch({ code: this.form.controls.postalCode.value, radius })
      .pipe(
        map((res) => res.data.getNearestFranchises),
        finalize(() => {
          this.loadingIndicatorService.hide();
          if (this.isReg) {
            this.cognitoService.signOut();
          }
        })
      )
      .subscribe((franchises) => {
        this.radioOptions = [];
        if (Array.isArray(franchises)) {
          franchises.forEach((f) => this.radioOptions.push(f));
        }
      });
  }

  private checkPostalCodePattern(group: FormGroup): { [k: string]: any } {
    const errors: { [k: string]: any } = {};
    const postalCode = group.controls.postalCode.value;

    if (!POSTAL_CODE_REGEX.test(postalCode)) {
      errors.postalCode = true;
    }

    return errors;
  }
}
